import { Injectable } from '@angular/core';
import * as semaphore from 'semaphore';
import { OfflineStorageTimestampService } from '../offline-storage-timestamp.service';
import SemaphoreHelper from '../../helpers/semaphore-helper';
import { CollectionHelper } from '../../helpers/collection-helper';
import { Observable } from 'rxjs';
import { OfflineAsset } from './offline-asset';
import { NgForage } from 'ngforage';
import AssetHelper from 'src/app/helpers/asset-helper';

@Injectable({
  providedIn: 'root',
})
export class PlacerProductChangesService {
  static readonly storageKeyPrefix = 'product-';
  static readonly timestampCollectionKey = 'products';
  static readonly offlineStorageMutex = semaphore(1);

  private expiredProductRemover: ExpiredProductRemover;

  constructor(
    private offlineStorageTimestampService: OfflineStorageTimestampService,
    private ngForage: NgForage
  ) {
    this.expiredProductRemover = new ExpiredProductRemover(
      this.offlineStorageTimestampService,
      this.ngForage
    );
    this.expiredProductRemover.removeExpiredItems();
  }

  static getProductStorageKey(id: number) {
    if (id == null) {
      throw new Error('id must not be null');
    }
    return this.storageKeyPrefix + id;
  }

  public getOfflineProductChangeObject(
    id: number
  ): Observable<OfflineProductChangeObject> {
    const stringified = localStorage.getItem(
      PlacerProductChangesService.getProductStorageKey(id)
    );
    if (stringified != null) {
      SemaphoreHelper.use(
        PlacerProductChangesService.offlineStorageMutex,
        async () => {
          this.offlineStorageTimestampService.setTimestamps(
            PlacerProductChangesService.timestampCollectionKey,
            [{ key: id.toString(), timestamp: Date.now() }]
          );
        }
      );
    }
    return new Observable((subscriber) =>
      subscriber.next(stringified == null ? null : JSON.parse(stringified))
    );
  }

  public getOfflineProductChangeObjects(
    ids: number[]
  ): Observable<OfflineProductChangeObject[]> {
    if (!CollectionHelper.isPopulated(ids)) {
      throw new Error('ids must contain at least one number');
    }
    const stringifiedObjects = ids
      .map((id) =>
        localStorage.getItem(
          PlacerProductChangesService.getProductStorageKey(id)
        )
      )
      .filter((o) => o != null);
    const toReturn = stringifiedObjects.map((o) => JSON.parse(o));
    if (toReturn.length > 0) {
      const now = Date.now();
      SemaphoreHelper.use(
        PlacerProductChangesService.offlineStorageMutex,
        async () => {
          this.offlineStorageTimestampService.setTimestamps(
            PlacerProductChangesService.timestampCollectionKey,
            toReturn.map((o) => {
              return { key: o.id.toString(), timestamp: now };
            })
          );
        }
      );
    }
    return new Observable((subscriber) => subscriber.next(toReturn));
  }

  public getDirectOfflineProductChangeObjects(
    ids: number[]
  ): OfflineProductChangeObject[] {
    if (!CollectionHelper.isPopulated(ids)) {
      throw new Error('ids must contain at least one number');
    }
    const stringifiedObjects = ids
      .map((id) =>
        localStorage.getItem(
          PlacerProductChangesService.getProductStorageKey(id)
        )
      )
      .filter((o) => o != null);
    const toReturn = stringifiedObjects.map((o) => JSON.parse(o));
    if (toReturn.length > 0) {
      const now = Date.now();
      SemaphoreHelper.use(
        PlacerProductChangesService.offlineStorageMutex,
        async () => {
          this.offlineStorageTimestampService.setTimestamps(
            PlacerProductChangesService.timestampCollectionKey,
            toReturn.map((o) => {
              return { key: o.id.toString(), timestamp: now };
            })
          );
        }
      );
    }
    return toReturn;
  }

  public async storeOfflineProductChangeObject(
    offlineProductChangeObject: OfflineProductChangeObject
  ): Promise<void> {
    if (
      offlineProductChangeObject == null ||
      offlineProductChangeObject.id == null
    ) {
      throw new Error(
        'offlineProductChangeObject must not be null and id must not be null'
      );
    }
    await SemaphoreHelper.use(
      PlacerProductChangesService.offlineStorageMutex,
      async () => {
        localStorage.setItem(
          PlacerProductChangesService.getProductStorageKey(
            offlineProductChangeObject.id
          ),
          JSON.stringify(offlineProductChangeObject)
        );
        this.offlineStorageTimestampService.setTimestamps(
          PlacerProductChangesService.timestampCollectionKey,
          [
            {
              key: offlineProductChangeObject.id.toString(),
              timestamp: Date.now(),
            },
          ]
        );
      }
    );
  }

  public async storeOfflineProductChangeObjects(
    offlineProductChangeObjects: OfflineProductChangeObject[]
  ): Promise<void> {
    if (!CollectionHelper.isPopulated(offlineProductChangeObjects)) {
      throw new Error('offlineProductChangeObjects must not be empty');
    }
    if (offlineProductChangeObjects.some((o) => o.id == null)) {
      throw new Error('id must not be null for any offlineProductChangeObjec');
    }
    await SemaphoreHelper.use(
      PlacerProductChangesService.offlineStorageMutex,
      async () => {
        for (const offlineProductChangeObject of offlineProductChangeObjects) {
          localStorage.setItem(
            PlacerProductChangesService.getProductStorageKey(
              offlineProductChangeObject.id
            ),
            JSON.stringify(offlineProductChangeObject)
          );
        }
        const timestamp = Date.now();
        this.offlineStorageTimestampService.setTimestamps(
          PlacerProductChangesService.timestampCollectionKey,
          offlineProductChangeObjects.map((o) => {
            return {
              key: o.id.toString(),
              timestamp: timestamp,
            };
          })
        );
      }
    );
  }

  public removeOfflineProductChangeObjects(
    offlineProductChangeObjects: OfflineProductChangeObject[]
  ): void {
    if (!CollectionHelper.isPopulated(offlineProductChangeObjects)) {
      return;
    }
    SemaphoreHelper.use(
      PlacerProductChangesService.offlineStorageMutex,
      async () => {
        offlineProductChangeObjects.forEach((o) => {
          PlacerProductChangesService.cleanupProductChangeObject(
            o,
            this.ngForage
          );
          localStorage.removeItem(
            PlacerProductChangesService.getProductStorageKey(o.id)
          );
        });
        this.offlineStorageTimestampService.removeItems(
          PlacerProductChangesService.timestampCollectionKey,
          offlineProductChangeObjects.map((o) => o.id.toString())
        );
      }
    );
  }

  public static async cleanupProductChangeObject(
    offlineProductChangeObject: OfflineProductChangeObject,
    ngForage: NgForage
  ) {
    if (
      offlineProductChangeObject != null &&
      CollectionHelper.isPopulated(offlineProductChangeObject.offlineAssets)
    ) {
      for (const offlineAsset of offlineProductChangeObject.offlineAssets) {
        await ngForage.removeItem(
          AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
        );
      }
    }
  }
}

export interface OfflineProductChangeObject {
  id: number;
  extraInfoVisibleDamage?: string;
  installerFeedback?: string;
  executionComplete?: boolean;
  executionDate?: Date;
  executionTime?: string;
  offlineAssets?: OfflineAsset[];
}

class ExpiredProductRemover {
  private readonly expiredItemRemovalInterval = 1800000;

  constructor(
    private offlineStorageTimestampService: OfflineStorageTimestampService,
    private ngForage: NgForage
  ) {}

  removeExpiredItems() {
    SemaphoreHelper.use(
      PlacerProductChangesService.offlineStorageMutex,
      async () => {
        const expiredItems =
          this.offlineStorageTimestampService.getExpiredItems(
            PlacerProductChangesService.timestampCollectionKey
          );
        if (CollectionHelper.isPopulated(expiredItems)) {
          expiredItems.forEach((i) => {
            const storageKey = PlacerProductChangesService.getProductStorageKey(
              Number(i.key)
            );
            const expiredItem = localStorage.getItem(storageKey);
            if (expiredItem != null) {
              PlacerProductChangesService.cleanupProductChangeObject(
                JSON.parse(expiredItem),
                this.ngForage
              );
            }
            localStorage.removeItem(storageKey);
          });
          this.offlineStorageTimestampService.removeItems(
            PlacerProductChangesService.timestampCollectionKey,
            expiredItems.map((i) => i.key)
          );
        }
        setTimeout(
          () => this.removeExpiredItems(),
          this.expiredItemRemovalInterval
        );
      }
    );
  }
}
